<script setup>
import { computed } from 'vue'
import { useStripeConnect } from '@/composables/useStripeConnect'
import FormButton from '@/components/Atoms/FormButton.vue'

// "Connect Stripe to monetize" prompt shown wherever a paid plan or a paid
// access level is about to be created while the owner's Stripe Connect
// account is not active. Drives the Paywall section's connect flow in place
// (new tab + polling) and emits `connected` when Stripe reports active.
const props = defineProps({
	// castr monetization.stripe: { connected, status, accountId, mode, requirements }
	stripe: {
		type: Object,
		default: () => ({ connected: false, status: 'none' }),
	},
	// short one-liner variant for modals / access pickers
	compact: {
		type: Boolean,
		default: false,
	},
	// what the owner was trying to do, e.g. "set paid access" / "create a paid plan"
	action: {
		type: String,
		default: 'monetize this site',
	},
})
const emit = defineEmits(['connected'])

const { connecting, failed, error, connect, reopen, cancel } = useStripeConnect({
	onConnected: () => emit('connected'),
})

const status = computed(() => props.stripe?.status || 'none')
const requirements = computed(() => props.stripe?.requirements || null)

const title = computed(() => {
	if (status.value === 'pending') return 'Finish your Stripe setup to monetize'
	if (status.value === 'mismatch') return 'This site is billed through a different Stripe account'
	if (status.value === 'disabled') return 'Your Stripe account is disabled'
	return 'Connect Stripe to monetize'
})

const description = computed(() => {
	if (status.value === 'pending') {
		const due = requirements.value?.currently_due?.length || requirements.value?.past_due?.length
		if (requirements.value?.under_review) return 'Stripe is reviewing your details. Paid plans unlock as soon as the account is approved.'
		return due ? 'Stripe still needs a few details before it can accept payments.' : 'Your Stripe onboarding is not complete yet.'
	}
	if (status.value === 'mismatch') return 'Your Paywall is connected to another Stripe account than the one this site was created with. Contact support to move the site.'
	if (status.value === 'disabled') return 'Stripe has disabled payouts for this account. Fix it in your Stripe dashboard, then retry.'
	return `Free and registration-only content works without Stripe. To ${props.action} you need an active Stripe Connect account — the same one your Paywall uses.`
})

const buttonLabel = computed(() => (status.value === 'pending' || status.value === 'disabled' ? 'Continue Stripe setup' : 'Connect Stripe'))
const canConnect = computed(() => status.value !== 'mismatch')
</script>

<template>
	<div class="rounded-lg border border-warning bg-warning-transparent" :class="compact ? 'px-3 py-2.5' : 'p-4'" data-testid="stripe-connect-prompt">
		<div class="flex flex-wrap items-start gap-3">
			<div class="min-w-0 flex-1">
				<p class="mb-0.5 font-medium text-white" :class="compact ? 'text-sm' : 'text-base'">{{ title }}</p>
				<p class="mb-0 text-tiny text-surface-8">{{ description }}</p>
				<p v-if="error" class="mb-0 mt-1 text-tiny text-error">{{ error }}</p>
				<p v-else-if="failed" class="mb-0 mt-1 text-tiny text-error">Stripe did not complete the link. Try again.</p>
			</div>
			<div v-if="canConnect" class="flex shrink-0 flex-wrap items-center gap-2">
				<template v-if="connecting">
					<span class="text-tiny text-surface-8">Finish in the Stripe tab — we're waiting…</span>
					<FormButton size="sm" type="secondary" @click="reopen">Reopen Stripe</FormButton>
					<FormButton size="sm" type="link" @click="cancel">Cancel</FormButton>
				</template>
				<FormButton v-else size="sm" @click="connect">{{ buttonLabel }}</FormButton>
			</div>
		</div>
	</div>
</template>
